import * as Sentry from '@sentry/react';
import { env } from '../util/env';
import { type ApiSource, type Brand, type Market, prettyPrintBrand } from '../context/claims';
import { realIframe } from '../../platform/iframe/realIframe';
import { getViewContext } from '../util/viewContext';
import { redactSensitiveDataDeep } from './redactSensitiveData';

const SentryEnvironment: Record<EnvMode, string | null> = {
  prod: 'Production',
  uat: 'UAT',
  mt: 'MT',
  dev: null,
};

const initiateInstrumentation = () => {
  const environment = SentryEnvironment[env.MODE];

  if (!environment) return;
  if (!env.VITE_SENTRY_SAMPLE_RATE) return;

  const client = Sentry.init({
    dsn: 'https://c0c491182b330628d6da98f1cd5d9f9a@o4511296547454976.ingest.de.sentry.io/4512067583541328',
    environment,
    debug: env.UAT,
    sampleRate: Number(env.VITE_SENTRY_SAMPLE_RATE),
    integrations: [
      Sentry.browserTracingIntegration({
        instrumentPageLoad: false,
        instrumentNavigation: false,
      }),
      Sentry.spanStreamingIntegration(),
      Sentry.featureFlagsIntegration(),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ['ecom-landing-page-frontend'],
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
      }),
    ],
    traceLifecycle: 'stream',
    tracesSampleRate: env.VITE_SENTRY_SAMPLE_RATE,
    beforeSend: redactSensitiveDataDeep,
    beforeSendSpan: Sentry.withStreamedSpan(redactSensitiveDataDeep),
    beforeBreadcrumb: redactSensitiveDataDeep,
    dataCollection: {
      userInfo: false,
      httpHeaders: {
        request: true,
        response: true,
      },
      httpBodies: [],
      urlQueryParams: true,
      genAI: {
        inputs: false,
        outputs: false,
      },
      databaseQueryData: false,
      stackFrameVariables: true,
    },
  });

  return client;
};

// this needs to initiate before any other imports in the app entry point. that's why it is initiate in the file directly.
// no router library here: view selection is `pathname` at boot plus `context.setRoute` in-app, so
// the browser tracing integration has nothing to hook into and the spans are driven by hand.
// https://docs.sentry.io/platforms/javascript/guides/react/tracing/instrumentation/automatic-instrumentation/
const client = initiateInstrumentation();

let pageLoadSpan =
  client &&
  Sentry.startBrowserTracingPageLoadSpan(client, {
    name: window.location.pathname,
    attributes: {
      [Sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: 'url',
    },
  });

export const trackRouteChange = (name: string) => {
  if (!client) return;

  if (pageLoadSpan) {
    pageLoadSpan.updateName(name);
    pageLoadSpan.setAttribute(Sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, 'route');
    pageLoadSpan = undefined;
    return;
  }

  Sentry.startBrowserTracingNavigationSpan(client, {
    op: 'navigation',
    name,
    attributes: {
      [Sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: 'route',
    },
  });
};

export const setSentryContext = (context: {
  brand: Brand;
  market?: Market;
  apiSource: ApiSource;
  hasAttemptedRedirect: boolean;
  noEdit: boolean;
  blockPhoneNumberInput: boolean;
}) => {
  if (!client) return;

  Sentry.setTags({
    brand: prettyPrintBrand(context.brand),
    market: context.market,
    apiSource: context.apiSource,
    hasAttemptedRedirect: context.hasAttemptedRedirect,
    noEdit: context.noEdit,
    blockPhoneNumberInput: context.blockPhoneNumberInput,
    viewContext: getViewContext(),
    widgetEmbed: realIframe.isWidgetSdkEmbed(),
  });
};

export const trackFeatureFlags = (flags: Record<string, boolean>) => {
  const integration = client?.getIntegrationByName<Sentry.FeatureFlagsIntegration>('FeatureFlags');
  if (!integration) return;

  for (const [name, value] of Object.entries(flags)) {
    integration.addFeatureFlag(name, value);
  }
};

export const getReactInstrumentation = () => {
  return {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Uncaught error', error, errorInfo.componentStack);
    }),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  } as const;
};
